/**
 * Patient Context Service
 * Fetches and formats patient data for Hope AI companion
 *
 * This service provides patient-specific context to Hope, including:
 * - Diagnoses (active and resolved)
 * - Prescriptions (active medications)
 * - Upcoming appointments
 * - Recent session history
 *
 * The data is formatted in a way that's easy for Convai to understand
 * and use in conversations.
 */

import { FirebasePatientService, Patient } from './firebase/FirebasePatientService';
import { FirebaseDiagnosisPrescriptionService, FirebaseDiagnosisRecord, FirebasePrescriptionRecord } from './firebase/FirebaseDiagnosisPrescriptionService';
import { firebaseAppointmentService } from './firebase/FirebaseAppointmentService';
import { db } from '../config/firebase';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import TeamMemberService from './TeamMemberService';

// Re-export compatible types for consumers
export type DiagnosisRecord = FirebaseDiagnosisRecord;
export type PrescriptionRecord = FirebasePrescriptionRecord;

interface Appointment {
  appointmentDate: string;
  startTime: string;
  modality: string;
  [key: string]: any;
}

export interface PatientContext {
  // Basic patient info
  patientId: string;
  patientName: string;
  age: number;

  // Provider info
  assignedProviderName?: string;
  assignedProviderId?: string;

  // Clinical data
  activeDiagnoses: DiagnosisRecord[];
  activePrescriptions: PrescriptionRecord[];
  upcomingAppointments: Appointment[];

  // Session history
  lastSessionDate?: string;
  totalSessions: number;

  // System context
  currentDateTime?: string;
  timezone?: string;
  location?: {
    latitude: number;
    longitude: number;
    city?: string;
    state?: string;
  };

  // Formatted text for Convai
  contextSummary: string;
}

export interface LastInteractionInfo {
  timestamp: Date;
  timeAgo: string;
  isFirstInteraction: boolean;
}

class PatientContextService {
  private static instance: PatientContextService;
  private teamMemberService: TeamMemberService;

  // Local storage key for tracking last interactions (non-sensitive, OK in localStorage)
  private static LAST_INTERACTION_KEY = 'hope_last_interactions';

  private constructor() {
    this.teamMemberService = TeamMemberService.getInstance();
  }

  public static getInstance(): PatientContextService {
    if (!PatientContextService.instance) {
      PatientContextService.instance = new PatientContextService();
    }
    return PatientContextService.instance;
  }

  /**
   * Get complete patient context for a given patient ID
   */
  public async getPatientContext(patientId: string): Promise<PatientContext | null> {
    const patient = await FirebasePatientService.getPatient(patientId);

    if (!patient) {
      console.error(`Patient not found: ${patientId}`);
      return null;
    }

    // Get diagnoses from Firestore
    const activeDiagnoses = await this.getActiveDiagnoses(patientId);

    // Get prescriptions from Firestore
    const activePrescriptions = await this.getActivePrescriptions(patientId);

    // Get upcoming appointments from Firestore
    const upcomingAppointments = await this.getUpcomingAppointments(patientId, 3);

    // Get session history from Firestore
    const sessions = await this.getSessionHistory(patientId);
    const lastSession = sessions[0]; // Most recent session

    // Calculate age
    const patientName = `${patient.firstName} ${patient.lastName}`;
    const age = this.calculateAge(patient.dateOfBirth);

    // Get assigned provider info
    let assignedProviderName: string | undefined;
    let assignedProviderId: string | undefined;
    if (patient.assignedProviderId) {
      assignedProviderId = patient.assignedProviderId;
      const provider = this.teamMemberService.getTeamMemberById(patient.assignedProviderId);
      if (provider) {
        assignedProviderName = provider.name;
      }
    }

    // Get current system time
    const now = new Date();
    const currentDateTime = now.toISOString();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Get location (optional - may require user permission)
    let location: PatientContext['location'] = undefined;
    try {
      location = await this.getCurrentLocation();
    } catch (error) {
      console.log('Location not available:', error);
      // Location is optional, continue without it
    }

    // Format context summary for Convai
    const contextSummary = this.formatContextSummary(
      patient,
      patientName,
      activeDiagnoses,
      activePrescriptions,
      upcomingAppointments,
      sessions,
      location,
      assignedProviderName
    );

    return {
      patientId: patient.id,
      patientName,
      age,
      assignedProviderName,
      assignedProviderId,
      activeDiagnoses,
      activePrescriptions,
      upcomingAppointments,
      lastSessionDate: lastSession?.sessionDate,
      totalSessions: sessions.length,
      currentDateTime,
      timezone,
      location,
      contextSummary
    };
  }

  /**
   * Get user's current location using browser Geolocation API
   * Requires user permission
   */
  private getCurrentLocation(): Promise<PatientContext['location']> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          // Optional: Reverse geocode to get city/state
          // For now, just return coordinates
          resolve({
            latitude,
            longitude,
            // city and state would require a geocoding API
          });
        },
        (error) => {
          reject(error);
        },
        {
          timeout: 5000,
          maximumAge: 300000, // Cache for 5 minutes
        }
      );
    });
  }

  /**
   * Format patient context into a human-readable summary for Convai
   * This text will be sent to Convai as character context
   */
  private formatContextSummary(
    patient: Patient,
    patientName: string,
    diagnoses: DiagnosisRecord[],
    prescriptions: PrescriptionRecord[],
    appointments: Appointment[],
    sessions: any[],
    location?: PatientContext['location'],
    assignedProviderName?: string
  ): string {
    const parts: string[] = [];

    // Current system time and date
    const now = new Date();

    // Determine timezone from patient's state
    let patientTimezone = 'America/New_York'; // Default to Eastern Time
    if (patient.state) {
      const stateTimezones: Record<string, string> = {
        'GA': 'America/New_York',
        'FL': 'America/New_York',
        'NY': 'America/New_York',
        'CA': 'America/Los_Angeles',
        'TX': 'America/Chicago',
        'IL': 'America/Chicago',
        'CO': 'America/Denver',
        'AZ': 'America/Phoenix',
      };
      patientTimezone = stateTimezones[patient.state] || 'America/New_York';
    }

    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: patientTimezone
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: patientTimezone
    };
    const currentDate = now.toLocaleDateString('en-US', dateOptions);
    const currentTime = now.toLocaleTimeString('en-US', timeOptions);

    // System context (time, date, timezone)
    parts.push(`Current system time: ${currentTime} on ${currentDate}.`);
    parts.push(`Patient's timezone: ${patientTimezone}.`);

    // Location context (use patient's address or browser geolocation)
    if (patient.city && patient.state) {
      parts.push(`Patient is located in ${patient.city}, ${patient.state} (${patient.zipCode || ''}).`);
    } else if (location) {
      parts.push(`Patient's current location: Latitude ${location.latitude.toFixed(4)}, Longitude ${location.longitude.toFixed(4)}.`);
      if (location.city && location.state) {
        parts.push(`Located in ${location.city}, ${location.state}.`);
      }
    }

    // Patient intro
    parts.push(`You are speaking with ${patientName}, a ${this.calculateAge(patient.dateOfBirth)}-year-old patient.`);

    // Assigned provider/therapist
    if (assignedProviderName) {
      parts.push(`Assigned therapist: ${assignedProviderName}.`);
    } else {
      parts.push('No therapist currently assigned.');
    }

    // Diagnoses
    if (diagnoses.length > 0) {
      const diagnosisNames = diagnoses.map(d => d.name).join(', ');
      parts.push(`Current diagnoses: ${diagnosisNames}.`);
    } else {
      parts.push('No active diagnoses on record.');
    }

    // Medications
    if (prescriptions.length > 0) {
      const medications = prescriptions.map(p =>
        `${p.medicationName} (${p.dosage}, ${p.frequency})`
      ).join(', ');
      parts.push(`Current medications: ${medications}.`);
    } else {
      parts.push('Not currently taking any medications.');
    }

    // Upcoming appointments
    if (appointments.length > 0) {
      const nextAppt = appointments[0];
      const apptDate = new Date(nextAppt.appointmentDate);
      const formattedDate = apptDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      parts.push(`Next appointment: ${formattedDate} at ${this.formatTime(nextAppt.startTime)} (${nextAppt.modality} session).`);

      if (appointments.length > 1) {
        parts.push(`${appointments.length - 1} additional appointment${appointments.length > 2 ? 's' : ''} scheduled.`);
      }
    } else {
      parts.push('No upcoming appointments scheduled.');
    }

    // Session history
    if (sessions.length > 0) {
      const lastSession = sessions[0];
      const lastDate = new Date(lastSession.sessionDate);
      const daysSince = Math.floor((Date.now() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

      if (daysSince === 0) {
        parts.push('Last therapy session was today.');
      } else if (daysSince === 1) {
        parts.push('Last therapy session was yesterday.');
      } else {
        parts.push(`Last therapy session was ${daysSince} days ago.`);
      }

      parts.push(`Total sessions: ${sessions.length}.`);
    } else {
      parts.push('This is a new patient with no previous therapy sessions.');
    }

    // Medical history note
    if (patient.medicalHistory && patient.medicalHistory !== 'No medical history provided') {
      parts.push(`Medical history note: ${patient.medicalHistory}`);
    }

    return parts.join(' ');
  }

  /**
   * Get a brief context string for page-specific interactions
   * Used when Hope opens on a specific page
   */
  public async getPageSpecificContext(patientId: string, currentPage: string): Promise<string> {
    const context = await this.getPatientContext(patientId);

    if (!context) {
      return `The patient is viewing the ${currentPage} page.`;
    }

    // Page-specific context
    switch (currentPage) {
      case 'diagnoses':
        if (context.activeDiagnoses.length > 0) {
          const diagnosisList = context.activeDiagnoses.map(d => d.name).join(', ');
          return `${context.patientName} is viewing their diagnoses page. Current diagnoses: ${diagnosisList}. They may have questions about their conditions.`;
        } else {
          return `${context.patientName} is viewing their diagnoses page. No active diagnoses are listed.`;
        }

      case 'prescriptions':
      case 'medications':
        if (context.activePrescriptions.length > 0) {
          const medList = context.activePrescriptions.map(p => p.medicationName).join(', ');
          return `${context.patientName} is viewing their medications page. Current medications: ${medList}. They may have questions about dosages, side effects, or how to take their medications.`;
        } else {
          return `${context.patientName} is viewing their medications page. No active prescriptions are listed.`;
        }

      case 'appointments':
        if (context.upcomingAppointments.length > 0) {
          const nextAppt = context.upcomingAppointments[0];
          const apptDate = new Date(nextAppt.appointmentDate).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric'
          });
          return `${context.patientName} is viewing their appointments page. Next appointment: ${apptDate} at ${this.formatTime(nextAppt.startTime)}. They may want to reschedule, prepare, or ask questions about their appointment.`;
        } else {
          return `${context.patientName} is viewing their appointments page. No upcoming appointments are scheduled. They may want to schedule a new appointment.`;
        }

      case 'dashboard':
      case 'home':
        return `${context.patientName} is on their dashboard. ${context.contextSummary}`;

      default:
        return `${context.patientName} is viewing the ${currentPage} page. ${context.contextSummary}`;
    }
  }

  /**
   * Get crisis-relevant context
   * Returns information that might be relevant if Hope detects crisis language
   */
  public async getCrisisContext(patientId: string): Promise<string> {
    const context = await this.getPatientContext(patientId);

    if (!context) {
      return 'Patient context unavailable.';
    }

    const parts: string[] = [];

    // Diagnoses that may be relevant to crisis situations
    const relevantDiagnoses = context.activeDiagnoses.filter(d =>
      d.name.toLowerCase().includes('depression') ||
      d.name.toLowerCase().includes('anxiety') ||
      d.name.toLowerCase().includes('bipolar') ||
      d.name.toLowerCase().includes('ptsd') ||
      d.name.toLowerCase().includes('trauma')
    );

    if (relevantDiagnoses.length > 0) {
      parts.push(`Patient has diagnoses: ${relevantDiagnoses.map(d => d.name).join(', ')}.`);
    }

    // Last therapy session
    if (context.lastSessionDate) {
      const daysSince = Math.floor((Date.now() - new Date(context.lastSessionDate).getTime()) / (1000 * 60 * 60 * 24));
      parts.push(`Last therapy session was ${daysSince} day${daysSince !== 1 ? 's' : ''} ago.`);
    }

    // Next appointment
    if (context.upcomingAppointments.length > 0) {
      const nextAppt = context.upcomingAppointments[0];
      const daysUntil = Math.floor((new Date(nextAppt.appointmentDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      parts.push(`Next appointment is in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}.`);
    } else {
      parts.push('No upcoming appointments scheduled.');
    }

    return parts.join(' ');
  }

  // ─── Firestore data helpers ───

  private async getActiveDiagnoses(patientId: string): Promise<DiagnosisRecord[]> {
    try {
      const q = query(
        collection(db, 'diagnoses'),
        where('patientId', '==', patientId),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DiagnosisRecord));
    } catch (error) {
      console.error('Failed to load diagnoses:', error);
      return [];
    }
  }

  private async getActivePrescriptions(patientId: string): Promise<PrescriptionRecord[]> {
    try {
      const q = query(
        collection(db, 'prescriptions'),
        where('patientId', '==', patientId),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as PrescriptionRecord));
    } catch (error) {
      console.error('Failed to load prescriptions:', error);
      return [];
    }
  }

  private async getUpcomingAppointments(patientId: string, limit: number): Promise<Appointment[]> {
    try {
      const now = new Date().toISOString().split('T')[0];
      const q = query(
        collection(db, 'appointments'),
        where('patientId', '==', patientId),
        where('appointmentDate', '>=', now),
        orderBy('appointmentDate', 'asc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.slice(0, limit).map(doc => ({ id: doc.id, ...doc.data() } as any as Appointment));
    } catch (error) {
      console.error('Failed to load appointments:', error);
      return [];
    }
  }

  private async getSessionHistory(patientId: string): Promise<any[]> {
    try {
      const q = query(
        collection(db, 'session_recordings'),
        where('patientId', '==', patientId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          sessionDate: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : data.createdAt,
          ...data
        };
      });
    } catch (error) {
      console.error('Failed to load session history:', error);
      return [];
    }
  }

  /**
   * Calculate age from date of birth
   */
  private calculateAge(dateOfBirth: string): number {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  /**
   * Format time from "HH:MM" to "h:MM AM/PM"
   */
  private formatTime(time: string): string {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  /**
   * Check if patient has any appointments in the next 24 hours
   * Useful for appointment reminders
   */
  public async hasUpcomingAppointmentIn24Hours(patientId: string): Promise<boolean> {
    const appointments = await this.getUpcomingAppointments(patientId, 5);
    const now = Date.now();
    const twentyFourHoursFromNow = now + (24 * 60 * 60 * 1000);

    return appointments.some(appt => {
      const apptTime = new Date(appt.appointmentDate).getTime();
      return apptTime >= now && apptTime <= twentyFourHoursFromNow;
    });
  }

  /**
   * Get next appointment details (for reminder notifications)
   */
  public async getNextAppointmentDetails(patientId: string): Promise<Appointment | null> {
    const appointments = await this.getUpcomingAppointments(patientId, 1);
    return appointments.length > 0 ? appointments[0] : null;
  }

  /**
   * Get the last interaction time for a patient with Hope
   * Returns human-readable time ago string for personalized greetings
   */
  public async getLastInteraction(patientId: string): Promise<LastInteractionInfo | null> {
    try {
      const storedData = localStorage.getItem(PatientContextService.LAST_INTERACTION_KEY);
      if (!storedData) {
        return null;
      }

      const interactions: Record<string, string> = JSON.parse(storedData);
      const lastTimestamp = interactions[patientId];

      if (!lastTimestamp) {
        return null;
      }

      const lastDate = new Date(lastTimestamp);
      const now = new Date();
      const diffMs = now.getTime() - lastDate.getTime();

      // Calculate time ago
      const timeAgo = this.formatTimeAgo(diffMs);

      return {
        timestamp: lastDate,
        timeAgo,
        isFirstInteraction: false
      };
    } catch (error) {
      console.error('Error getting last interaction:', error);
      return null;
    }
  }

  /**
   * Record a new interaction with Hope for a patient
   */
  public async recordInteraction(patientId: string): Promise<void> {
    try {
      const storedData = localStorage.getItem(PatientContextService.LAST_INTERACTION_KEY);
      const interactions: Record<string, string> = storedData ? JSON.parse(storedData) : {};

      interactions[patientId] = new Date().toISOString();

      localStorage.setItem(PatientContextService.LAST_INTERACTION_KEY, JSON.stringify(interactions));
      console.log(`Recorded Hope interaction for patient ${patientId}`);
    } catch (error) {
      console.error('Error recording interaction:', error);
    }
  }

  /**
   * Format milliseconds into a human-readable "time ago" string
   */
  private formatTimeAgo(diffMs: number): string {
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(days / 7);

    if (minutes < 1) {
      return 'just now';
    } else if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (days === 1) {
      return 'yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    } else if (weeks === 1) {
      return 'about a week ago';
    } else if (weeks < 4) {
      return `${weeks} weeks ago`;
    } else {
      // More than a month, show actual date
      const date = new Date(Date.now() - diffMs);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    }
  }

  /**
   * Get personalized greeting based on last interaction
   * Returns a greeting suggestion for Hope to use
   */
  public async getPersonalizedGreeting(patientId: string): Promise<string> {
    const patient = await FirebasePatientService.getPatient(patientId);
    const firstName = patient?.firstName || 'there';

    const lastInteraction = await this.getLastInteraction(patientId);

    if (!lastInteraction) {
      // First time user
      return `Welcome, ${firstName}! It's so nice to meet you. I'm Hope, and I'm here to support you between your therapy sessions.`;
    }

    // Personalized based on time since last visit
    const { timeAgo } = lastInteraction;

    if (timeAgo === 'just now' || timeAgo.includes('minute')) {
      return `Welcome back, ${firstName}! I'm glad you're here again.`;
    } else if (timeAgo.includes('hour')) {
      return `Welcome back, ${firstName}! Good to see you again today.`;
    } else if (timeAgo === 'yesterday') {
      return `Welcome back, ${firstName}! It's nice to see you again. How are you doing today?`;
    } else if (timeAgo.includes('day')) {
      return `Welcome back, ${firstName}! I've been thinking of you. It's been ${timeAgo} since we last talked.`;
    } else {
      return `Welcome back, ${firstName}! It's been a little while since we last connected. I'm glad you're here.`;
    }
  }
}

// Export singleton instance
export const patientContextService = PatientContextService.getInstance();
export default PatientContextService;
